import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import apiService from '@/services/api'
import type { User, LoginCredentials, RegisterData, UpdateUserData, ChangePasswordData } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const isLoading = ref(false)
  const toast = useToast()

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')

  const login = async (credentials: LoginCredentials) => {
    isLoading.value = true
    try {
      const response = await apiService.login(credentials)
      
      user.value = response.user
      token.value = response.token
      
      localStorage.setItem('auth_token', response.token)
      localStorage.setItem('auth_user', JSON.stringify(response.user))
      localStorage.setItem('auth_expires', response.expiresAt)
      
      toast.success('Login successful!')
      return { success: true, user: response.user }
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Error al iniciar sesión'
      // Don't show toast here, let the component handle the error display
      throw new Error(message)
    } finally {
      isLoading.value = false
    }
  }

  const register = async (data: RegisterData) => {
    isLoading.value = true
    try {
      const response = await apiService.register(data)
      
      user.value = response.user
      token.value = response.token
      
      localStorage.setItem('auth_token', response.token)
      localStorage.setItem('auth_user', JSON.stringify(response.user))
      localStorage.setItem('auth_expires', response.expiresAt)
      
      toast.success('Registration successful!')
      return { success: true, user: response.user }
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Error en el registro'
      toast.error(message)
      throw new Error(message)
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      if (token.value) {
        await apiService.logout()
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      user.value = null
      token.value = null
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
      localStorage.removeItem('auth_expires')
      toast.info('Logged out successfully')
    }
  }

  const refreshUser = async () => {
    try {
      if (token.value) {
        const userData = await apiService.getCurrentUser()
        user.value = userData
        localStorage.setItem('auth_user', JSON.stringify(userData))
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error)
      await logout()
    }
  }

  const initializeAuth = () => {
    const savedToken = localStorage.getItem('auth_token')
    const savedUser = localStorage.getItem('auth_user')
    const savedExpires = localStorage.getItem('auth_expires')
    
    if (savedToken && savedUser && savedExpires) {
      const expiresAt = new Date(savedExpires)
      const now = new Date()
      
      if (expiresAt > now) {
        token.value = savedToken
        user.value = JSON.parse(savedUser)
      } else {
        // Token expired, clear storage
        localStorage.removeItem('auth_token')
        localStorage.removeItem('auth_user')
        localStorage.removeItem('auth_expires')
      }
    }
  }

  const updateProfile = async (data: UpdateUserData) => {
    if (!user.value) return
    
    try {
      const updatedUser = await apiService.updateUser(user.value.id, data)
      user.value = updatedUser
      localStorage.setItem('auth_user', JSON.stringify(updatedUser))
      toast.success('Profile updated successfully!')
      return updatedUser
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update profile'
      toast.error(message)
      throw new Error(message)
    }
  }

  const changePassword = async (data: ChangePasswordData) => {
    if (!user.value) return
    
    try {
      await apiService.changePassword(user.value!.id, data)
      toast.success('Password changed successfully!')
      return { success: true }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to change password'
      toast.error(message)
      throw new Error(message)
    }
  }

  const checkAuth = async () => {
    if (!token.value) {
      throw new Error('No token available')
    }
    
    try {
      const userData = await apiService.getCurrentUser()
      user.value = userData
      localStorage.setItem('auth_user', JSON.stringify(userData))
      return { success: true }
    } catch (error: any) {
      await logout()
      throw new Error('Session expired')
    }
  }

  const refreshToken = async () => {
    if (!token.value) {
      throw new Error('No token to refresh')
    }
    
    try {
      // For now, just check if the current token is still valid
      await checkAuth()
      return { success: true }
    } catch (error: any) {
      throw new Error('Failed to refresh token')
    }
  }

  return {
    user,
    token,
    isLoading,
    isAuthenticated,
    isAdmin,
    login,
    register,
    logout,
    refreshUser,
    initializeAuth,
    updateProfile,
    changePassword,
    checkAuth,
    refreshToken
  }
})